import React, { useRef, useCallback } from "react";
import {
  ReloadRoomDesignIcon,
  UndoIcon,
  RedoIcon,
} from "../../SVGIcons";
import { EditorTool, EditorObject } from "../types/editorTypes";
import { ToolSelectionButtons } from "./ToolSelectionButtons";
import { MarkerToolOptions } from "./MarkerToolOptions";
import { RectangleToolOptions } from "./RectangleToolOptions";
import { TextFormattingOptions } from "./TextFormattingOptions";
import { SelectedMarkerOptions } from "./SelectedMarkerOptions";

interface EditorToolbarProps {
  // ツール関連
  currentTool: EditorTool;
  setCurrentTool: (tool: EditorTool) => void;
  // 選択解除コールバック
  clearSelection?: () => void;
  // マーカー設定
  markerColor: string;
  setMarkerColor: (color: string) => void;
  markerStrokeWidth: 1 | 2 | 3 | 4 | 8 | 16 | 32;
  setMarkerStrokeWidth: (width: 1 | 2 | 3 | 4 | 8 | 16 | 32) => void;
  // 四角形設定
  rectFillEnabled: boolean;
  setRectFillEnabled: (enabled: boolean) => void;
  rectFillColor: string;
  setRectFillColor: (color: string) => void;
  rectStrokeEnabled: boolean;
  setRectStrokeEnabled: (enabled: boolean) => void;
  rectStrokeColor: string;
  setRectStrokeColor: (color: string) => void;
  // テキスト設定
  textFontFamily: string;
  setTextFontFamily: (family: string) => void;
  textFontSize: number;
  setTextFontSize: (size: number) => void;
  textFontStyle: 'normal' | 'bold' | 'italic';
  setTextFontStyle: (style: 'normal' | 'bold' | 'italic') => void;
  textAlign: 'left' | 'center' | 'right' | 'justify';
  setTextAlign: (align: 'left' | 'center' | 'right' | 'justify') => void;
  verticalAlign: 'top' | 'middle' | 'bottom';
  setVerticalAlign: (align: 'top' | 'middle' | 'bottom') => void;
  textColor: string;
  setTextColor: (color: string) => void;
  textBgColor: string;
  setTextBgColor: (color: string) => void;
  // 選択中のオブジェクト
  selectedTextObject: EditorObject | null;
  selectedRectObject: EditorObject | null;
  selectedMarkerObject: EditorObject | null;
  // プロパティ更新関数
  updateTextProperty: (property: keyof EditorObject, value: any) => void;
  updateRectProperty: (property: keyof EditorObject, value: any) => void;
  updateMarkerProperty: (property: keyof EditorObject, value: any) => void;
  // アクション
  onReset: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onAddImage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // ボタンの有効/無効状態
  canReset: boolean;
  canUndo: boolean;
  canRedo: boolean;
}

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  currentTool,
  setCurrentTool,
  clearSelection,
  markerColor,
  setMarkerColor,
  markerStrokeWidth,
  setMarkerStrokeWidth,
  rectFillEnabled,
  setRectFillEnabled,
  rectFillColor,
  setRectFillColor,
  rectStrokeEnabled,
  setRectStrokeEnabled,
  rectStrokeColor,
  setRectStrokeColor,
  textFontFamily,
  setTextFontFamily,
  textFontSize,
  setTextFontSize,
  textFontStyle,
  setTextFontStyle,
  textAlign,
  setTextAlign,
  verticalAlign,
  setVerticalAlign,
  textColor,
  setTextColor,
  textBgColor,
  setTextBgColor,
  selectedTextObject,
  selectedRectObject,
  selectedMarkerObject,
  updateTextProperty,
  updateRectProperty,
  updateMarkerProperty,
  onReset,
  onUndo,
  onRedo,
  onAddImage,
  canReset,
  canUndo,
  canRedo,
}) => {
  const addImageInputRef = useRef<HTMLInputElement>(null);

  // ツール変更時に選択を解除するラッパー関数
  const handleToolChange = useCallback((tool: EditorTool) => {
    setCurrentTool(tool);
    // 描画ツール（select以外）が選択された場合は選択を解除
    if (tool !== 'select' && clearSelection) {
      clearSelection();
    }
  }, [setCurrentTool, clearSelection]);

  return (
    <div className="flex items-center gap-1 p-2 bg-white border-b border-gray-300">
      {/* 履歴操作 */}
      <button
        onClick={onReset}
        disabled={!canReset}
        className={`p-1.5 rounded ${canReset ? 'hover:bg-gray-100' : 'cursor-not-allowed'}`}
        title="最初から"
        style={{ opacity: canReset ? 1 : 0.3 }}
      >
        <ReloadRoomDesignIcon className="w-5 h-5 !mr-0" />
      </button>
      <button
        onClick={onUndo}
        disabled={!canUndo}
        className={`p-1.5 rounded ${canUndo ? 'hover:bg-gray-100' : 'cursor-not-allowed'}`}
        title="戻す"
        style={{ opacity: canUndo ? 1 : 0.3 }}
      >
        <UndoIcon className="w-5 h-5 fill-current" />
      </button>
      <button
        onClick={onRedo}
        disabled={!canRedo}
        className={`p-1.5 rounded ${canRedo ? 'hover:bg-gray-100' : 'cursor-not-allowed'}`}
        title="やり直し"
        style={{ opacity: canRedo ? 1 : 0.3 }}
      >
        <RedoIcon className="w-5 h-5 fill-current" />
      </button>

      <div className="w-[1px] h-6 bg-gray-400 mx-2" />

      {/* ツール選択ボタン */}
      <ToolSelectionButtons
        currentTool={currentTool}
        setCurrentTool={handleToolChange}
        onAddImageClick={() => addImageInputRef.current?.click()}
      />
      <input
        ref={addImageInputRef}
        type="file"
        accept="image/png, image/jpeg, image/webp"
        className="hidden"
        onChange={onAddImage}
      />

      <div className="w-[1px] h-6 bg-gray-400 mx-2" />
      <div className="w-2" />

      {/* マーカー・水平線ツール選択時のオプション */}
      {(currentTool === 'marker' || currentTool === 'horizontal') && (
        <MarkerToolOptions
          markerColor={markerColor}
          setMarkerColor={setMarkerColor}
          markerStrokeWidth={markerStrokeWidth}
          setMarkerStrokeWidth={setMarkerStrokeWidth}
        />
      )}

      {/* マーカー・線オブジェクト選択時の編集オプション */}
      {selectedMarkerObject && (
        <SelectedMarkerOptions
          selectedMarkerObject={selectedMarkerObject}
          updateMarkerProperty={updateMarkerProperty}
        />
      )}

      {/* テキストツール選択時 or 四角ツール選択時 or テキスト/四角形オブジェクト選択時の書式設定 */}
      {(currentTool === 'text' || currentTool === 'rectangle' || selectedTextObject || selectedRectObject) && (
        <TextFormattingOptions
          textFontFamily={textFontFamily}
          setTextFontFamily={setTextFontFamily}
          textFontSize={textFontSize}
          setTextFontSize={setTextFontSize}
          textFontStyle={textFontStyle}
          setTextFontStyle={setTextFontStyle}
          textAlign={textAlign}
          setTextAlign={setTextAlign}
          verticalAlign={verticalAlign}
          setVerticalAlign={setVerticalAlign}
          textColor={textColor}
          setTextColor={setTextColor}
          textBgColor={textBgColor}
          setTextBgColor={setTextBgColor}
          selectedTextObject={selectedTextObject}
          selectedRectObject={selectedRectObject}
          updateTextProperty={updateTextProperty}
          updateRectProperty={updateRectProperty}
        />
      )}
    </div>
  );
};

export default EditorToolbar;
